import React, { useContext } from 'react';
import { Button, ButtonProps } from '@cloudscape-design/components';
import { AccountContext } from 'src/context/accountContext';

export type PermissionLevel =
  | 'READ_ONLY'
  | 'READ_AND_LIMITED_ACTION'
  | 'ADMINISTER';

export interface PermissionAwareButtonProps extends ButtonProps {
  requiredPermission: PermissionLevel;
  actionDescription: string;
}

// Map permission levels to numeric values for easier comparison
// Higher number = more permissions
const permissionLevelToValue: Record<PermissionLevel, number> = {
  READ_ONLY: 1,
  READ_AND_LIMITED_ACTION: 2,
  ADMINISTER: 3,
};

// Map permission levels to user-friendly descriptions
const permissionLevelToFriendlyName: Record<PermissionLevel, string> = {
  READ_ONLY: 'read-only',
  READ_AND_LIMITED_ACTION: 'limited action',
  ADMINISTER: 'administrator',
};

export const PermissionAwareButton: React.FC<PermissionAwareButtonProps> = ({
  requiredPermission,
  actionDescription,
  ...buttonProps
}) => {
  const { selectedAccount } = useContext(AccountContext);
  const userPermission = selectedAccount?.permissionLevel || 'ADMINISTER';

  // Check if user has required permission using numeric comparison
  const hasPermission = () => {
    const userPermissionValue =
      permissionLevelToValue[userPermission] ||
      permissionLevelToValue.ADMINISTER;
    const requiredPermissionValue = permissionLevelToValue[requiredPermission];

    return userPermissionValue >= requiredPermissionValue;
  };

  // Generate appropriate disabled reason message
  const getDisabledReason = () => {
    if (!hasPermission()) {
      const friendlyName =
        permissionLevelToFriendlyName[requiredPermission] || 'appropriate';
      return `You need ${friendlyName} permissions to ${actionDescription}`;
    }
    return undefined;
  };

  return (
    <Button
      {...buttonProps}
      disabled={buttonProps.disabled || !hasPermission()}
      disabledReason={buttonProps.disabledReason || getDisabledReason()}
    />
  );
};
